(() => {
  if (customElements.get('featured-articles-slider')) return;

  class FeaturedArticlesSlider extends HTMLElement {
    constructor() {
      super();
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onResize = this.onResize.bind(this);
      this.updateNavPosition = this.updateNavPosition.bind(this);
      this.swiper = null;
      this.viewportEl = null;
      this.navPositionObserver = null;
    }

    connectedCallback() {
      this.sliderEl = this.querySelector('[data-slider]');
      this.viewportEl = this.querySelector('.featured-articles__viewport');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.paginationEl = this.querySelector('[data-pagination]');

      if (!this.sliderEl) return;

      this.classList.remove('is-initialized');

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }

      window.addEventListener('resize', this.onResize);

      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', this.onWindowLoad, { once: true });
        return;
      }

      this.initSwiper();
    }

    disconnectedCallback() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
      }

      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('resize', this.onResize);
      this.teardownNavPositionObserver();
      this.destroySwiper();
    }

    onWindowLoad() {
      this.initSwiper();
    }

    onSectionLoad(event) {
      if (!event.target.contains(this)) return;
      this.destroySwiper();
      this.initSwiper();
    }

    applySlidesOffsetAfter(swiper) {
      if (!swiper?.params) return;

      const width = window.innerWidth;

      if (width >= 1024) {
        swiper.params.slidesOffsetAfter = 0;
        return;
      }

      if (width >= 768) {
        swiper.params.slidesOffsetAfter = 20;
        return;
      }

      swiper.params.slidesOffsetAfter = 16;
    }

    onResize() {
      if (!this.swiper) return;
      this.applySlidesOffsetAfter(this.swiper);
      this.swiper.update();
      this.updateNavPosition();
    }

    getNavMediaElement() {
      const activeSlide = this.sliderEl?.querySelector('.swiper-slide-active');
      const activeMedia = activeSlide?.querySelector('.featured-articles__media');

      if (activeMedia) return activeMedia;

      return this.sliderEl?.querySelector('.featured-articles__media');
    }

    updateNavPosition() {
      if (!this.viewportEl) return;

      const media = this.getNavMediaElement();

      if (!media) {
        this.viewportEl.style.removeProperty('--fa-nav-top');
        return;
      }

      const viewportRect = this.viewportEl.getBoundingClientRect();
      const mediaRect = media.getBoundingClientRect();
      const topOffset = mediaRect.top - viewportRect.top + mediaRect.height / 2;

      this.viewportEl.style.setProperty('--fa-nav-top', `${topOffset}px`);
    }

    setupNavPositionObserver() {
      this.teardownNavPositionObserver();

      if (!this.viewportEl || typeof ResizeObserver === 'undefined') {
        this.updateNavPosition();
        return;
      }

      this.navPositionObserver = new ResizeObserver(() => {
        this.updateNavPosition();
      });

      this.navPositionObserver.observe(this.viewportEl);

      const media = this.getNavMediaElement();
      if (media) {
        this.navPositionObserver.observe(media);
      }

      const mediaImage = media?.querySelector('.featured-articles__media-image');
      if (mediaImage && !mediaImage.complete) {
        mediaImage.addEventListener('load', this.updateNavPosition, { once: true });
      }

      this.updateNavPosition();
    }

    teardownNavPositionObserver() {
      this.navPositionObserver?.disconnect();
      this.navPositionObserver = null;
    }

    initSwiper() {
      if (typeof Swiper === 'undefined' || this.swiper || !this.sliderEl) return;

      const slideCount = this.sliderEl.querySelectorAll('.swiper-slide').length;
      if (!slideCount) return;

      this.swiper = new Swiper(this.sliderEl, {
        slidesPerView: 1.2273,
        slidesPerGroup: 1,
        spaceBetween: 16,
        breakpointsInverse: true,
        watchOverflow: true,
        allowTouchMove: slideCount > 1,
        simulateTouch: slideCount > 1,
        preventClicks: true,
        preventClicksPropagation: true,
        touchStartPreventDefault: false,
        threshold: 5,
        navigation:
          slideCount > 1
            ? {
                prevEl: this.prevButton,
                nextEl: this.nextButton,
              }
            : false,
        pagination:
          slideCount > 1 && this.paginationEl
            ? {
                el: this.paginationEl,
                clickable: true,
                bulletElement: 'button',
                bulletClass: 'featured-articles__pagination-bullet',
                bulletActiveClass: 'featured-articles__pagination-bullet--active',
              }
            : false,
        breakpoints: {
          600: {
            slidesPerView: 1.8,
            slidesPerGroup: 1,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 2.2,
            slidesPerGroup: 1,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            slidesPerGroup: 1,
            spaceBetween: 24,
          },
        },
        on: {
          breakpoint: () => {
            if (!this.swiper) return;
            this.applySlidesOffsetAfter(this.swiper);
            this.swiper.update();
            this.setupNavPositionObserver();
          },
          init: () => {
            this.setupNavPositionObserver();
          },
          slideChangeTransitionEnd: () => {
            this.updateNavPosition();
          },
          resize: () => {
            this.updateNavPosition();
          },
        },
      });

      this.applySlidesOffsetAfter(this.swiper);
      requestAnimationFrame(() => {
        this.swiper?.update();
        this.setupNavPositionObserver();
        this.classList.add('is-initialized');
      });
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
      this.teardownNavPositionObserver();
      this.classList.remove('is-initialized');
    }
  }

  customElements.define('featured-articles-slider', FeaturedArticlesSlider);
})();
